import moment from "moment";
import { type NumberOptions } from "vue-i18n";
import { formatNumber, formatString, hasValue } from "~/helpers/common-helpers";
import { capitalize, capitalizeAll, monthYear, dateRange, year } from "~/helpers/filter-helpers";
import { convertArea } from "~/helpers/entities/property-helpers";

export type FilterFunction<T = string | number> = (v: Nullable<T>) => string;

export const useFilter = () => {
  const i18n = useNuxtApp().$i18n.global;

  return {
    quarter: (input: Nullable<string>) =>
      formatString(input, (input) => {
        const momentDate = moment(input);
        return `Q${momentDate.quarter()} ${momentDate.year()}`;
      }),
    month: (input: Nullable<string>) => formatString(input, (input) => moment(input).format("MMM")),
    year,
    monthYear,
    dayMonthYear: (input: Nullable<string>) => formatString(input, (input) => moment(input).format("DD MMM YYYY")),
    integer: (number: number | string | null | undefined) => formatNumber(number, (number) => (number || 0).toFixed(0)),
    area: (number: number | string | null | undefined, unit?: Nullable<string>, { per = false, convertFrom }: { per?: boolean; convertFrom?: string } = {}) => {
      const translateUnit = useTranslateUnit();
      const localeUnit = unit ? translateUnit(unit) : undefined;

      return formatNumber(number, (number) => {
        if (convertFrom && unit) number = convertArea(number, convertFrom, unit);

        return localeUnit ? `${new Intl.NumberFormat(i18n.locale).format(number)}${per ? ` /` : ""} ${localeUnit}` : "–";
      });
    },
    currency: (
      number: number | string | null | undefined,
      {
        currency = "DKK",
        mode = "standard",
        maxDigits,
        minDigits,
        maxSignificants,
        perAreaUnit,
        roundingIncrement,
      }: {
        currency?: string;
        mode?: "standard" | "kvm" | "compact";
        roundingIncrement?: 1000;
        maxSignificants?: number;
        maxDigits?: number;
        minDigits?: number;
        perAreaUnit?: string;
      } = {}
    ) => {
      const formatter = (number: number) => {
        const result = new Intl.NumberFormat(i18n.locale, {
          style: "currency",
          currency,
          maximumSignificantDigits: hasValue(maxSignificants) ? maxSignificants : undefined,
          maximumFractionDigits: hasValue(maxDigits) ? maxDigits : mode === "compact" ? 1 : 0,
          minimumFractionDigits: minDigits ? minDigits : 0,
          roundingIncrement: hasValue(roundingIncrement) ? roundingIncrement : undefined,
          notation: mode === "compact" ? "compact" : undefined,
        }).format(number);
        const translateUnit = useTranslateUnit();

        const localePerAreaUnit = perAreaUnit ? translateUnit(perAreaUnit) : undefined;

        return perAreaUnit ? `${result} / ${localePerAreaUnit}` : result;
      };

      return formatNumber(number, formatter);
    },
    percentage: {
      standard: (number: number | string | null | undefined, { maximumFractionDigits = 0, minimumFractionDigits = 0, ...rest }: NumberOptions = {}) => {
        const formatter = (number: number) =>
          new Intl.NumberFormat(i18n.locale, { maximumFractionDigits, minimumFractionDigits, ...rest }).format(number) + "%";

        return formatNumber(number, formatter);
      },
      decimal: (number: number | string | null | undefined) => {
        const formatter = (number: number) => new Intl.NumberFormat(i18n.locale).format(number * 100) + "%";

        return formatNumber(number, formatter);
      },
      compact: (number: number | string | null | undefined, { minDigits = 1 } = {}) => {
        const formatter = (number: number) =>
          new Intl.NumberFormat(i18n.locale, {
            maximumFractionDigits: 1,
            minimumFractionDigits: minDigits,
          }).format(number) + "%";

        return formatNumber(number, formatter);
      },
    },
    capitalize,
    capitalizeAll,
    uppercase: <T extends string>(s: T) => s.toUpperCase() as Uppercase<T>,
    lowercase: <T extends string>(s: T) => s.toLowerCase() as Lowercase<T>,
    date: {
      short: (date: string | null | undefined) => formatString(date, (date) => moment(date, "YYYY-MM-DD").local().format("Do MMM YYYY")),
      long: (date: string | null | undefined) => formatString(date, (date) => moment(date).local().format("Do MMM YYYY, HH:mm:ss")),
      range: dateRange,
    },
    timefrom: (date: string | null | undefined) =>
      formatString(date, (date) => {
        const diff = Math.round(moment().diff(moment(date, "YYYY-MM-DD"), "months", true));
        const diffMonths = diff % 12;
        const diffYears = (diff - diffMonths) / 12;

        let output = [];

        if (diffYears !== 0) {
          output.push(`${diffYears} ${i18n.t("TIMEUNIT_YEAR", diffYears)}`);
        }

        if (diffMonths !== 0) {
          output.push(`${diffMonths} ${i18n.t("TIMEUNIT_MONTH", diffMonths)}`);
        }

        return output.join(", ");
      }),
    number: (number: Nullable<number | string>, { maximumFractionDigits = 0, minimumFractionDigits = 0, ...rest }: NumberOptions = {}) =>
      formatNumber(number, (number) => new Intl.NumberFormat(i18n.locale, { maximumFractionDigits, minimumFractionDigits, ...rest }).format(number)),
    string: (input: any) => `${input ?? ""}`,
    limitCharacters: (input: string, limit: number) => {
      let s = input || "";

      if (s.length > limit) {
        s = s.substring(0, limit);

        if (s[limit - 1] === " ") {
          s = s.substring(0, limit - 1);
        }

        return `${s.substring(0, limit)}…`;
      }

      return s;
    },
    initials: (input: Nullable<string>) => {
      input ??= "";

      const parts = input.split(" ");

      if (parts.length === 1) {
        return parts[0].charAt(0);
      }

      return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
    },
  };
};
